import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import MenuPaintedButton from './MenuPaintedButton';
import {
  WAR_EFFORT_LEAVE_MISSION_BUTTON_PATH,
  WAR_EFFORT_PRIMARY_BUTTON_PATH,
} from '../config/welcomeAssets';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './WarEffortEliteDefenseDialog.css';

const ELITE_FORCE_DIALOG_ART = '/images/417.jpg';

function formatCountdown(expiresAt) {
  if (expiresAt == null) {
    throw new Error('WarEffortEliteDefenseDialog: expiresAt is required');
  }
  const remaining = Math.max(0, Math.ceil((Number(expiresAt) - Date.now()) / 1000));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function SwordGlyph({ bladeFill, hiltFill }) {
  if (!bladeFill || !hiltFill) {
    throw new Error('SwordGlyph: bladeFill and hiltFill are required');
  }
  return (
    <>
      <path
        d="M120 6 L129 78 L120 88 L111 78 Z"
        fill={bladeFill}
        stroke="#2a1c0a"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M120 14 L120 76" stroke="#f8f1cc" strokeWidth="1.4" opacity="0.55" />
      <rect
        x="94"
        y="76"
        width="52"
        height="9"
        rx="2.5"
        fill={hiltFill}
        stroke="#2a1c0a"
        strokeWidth="1.3"
      />
      <rect x="113.5" y="85" width="13" height="34" rx="2" fill="#4a2c12" stroke="#1c1006" strokeWidth="1.1" />
      <path d="M113.5 91 H126.5 M113.5 97 H126.5 M113.5 103 H126.5 M113.5 109 H126.5" stroke="#d4b24a" strokeWidth="1.1" />
      <circle cx="120" cy="124" r="8.5" fill={hiltFill} stroke="#2a1c0a" strokeWidth="1.3" />
    </>
  );
}

function CrossedSwordsHeader() {
  const uid = React.useId().replace(/:/g, '');
  const bladeId = `we-sword-blade-${uid}`;
  const hiltId = `we-sword-hilt-${uid}`;
  const glowId = `we-sword-glow-${uid}`;
  return (
    <div className="we-elite-defense-swords" aria-hidden="true">
      <svg viewBox="0 0 240 150" className="we-elite-defense-swords-svg">
        <defs>
          <linearGradient id={bladeId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f6efd4" />
            <stop offset="40%" stopColor="#e0c86a" />
            <stop offset="100%" stopColor="#8a6a16" />
          </linearGradient>
          <linearGradient id={hiltId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#5c1610" />
            <stop offset="50%" stopColor="#e0c56a" />
            <stop offset="100%" stopColor="#5c1610" />
          </linearGradient>
          <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.4" floodColor="#000000" floodOpacity="0.75" />
            <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#c9a227" floodOpacity="0.45" />
          </filter>
        </defs>
        <g filter={`url(#${glowId})`}>
          <g transform="rotate(-40 120 80)">
            <SwordGlyph bladeFill={`url(#${bladeId})`} hiltFill={`url(#${hiltId})`} />
          </g>
          <g transform="rotate(40 120 80)">
            <SwordGlyph bladeFill={`url(#${bladeId})`} hiltFill={`url(#${hiltId})`} />
          </g>
        </g>
      </svg>
    </div>
  );
}

export function WarEffortEliteDefenseWaitingOverlay({ wait, t }) {
  const [clock, setClock] = useState(() => formatCountdown(wait.expiresAt));
  useEffect(() => {
    setClock(formatCountdown(wait.expiresAt));
    const id = setInterval(() => setClock(formatCountdown(wait.expiresAt)), 250);
    return () => clearInterval(id);
  }, [wait.expiresAt]);

  return createPortal(
    <div className="we-elite-defense-overlay we-elite-defense-waiting" data-block-global-esc-back>
      <div
        className="we-elite-defense-dialog"
        style={{ backgroundImage: `url('${ELITE_FORCE_DIALOG_ART}')` }}
      >
        <div className="we-elite-defense-dialog-content">
          <CrossedSwordsHeader />
          <h3>{t('warEffort.eliteDefenseWaiting', { time: clock })}</h3>
          <p>{wait.countryName}</p>
          <div className="we-elite-defense-countdown">{clock}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function WarEffortEliteDefenseDialog({ offer, onOk, onRefuse, busy, t, countryDisplayName, actionError }) {
  if (!offer) {
    throw new Error('WarEffortEliteDefenseDialog: offer is required');
  }
  if (typeof onOk !== 'function' || typeof onRefuse !== 'function') {
    throw new Error('WarEffortEliteDefenseDialog: onOk and onRefuse are required');
  }
  const [clock, setClock] = useState(() => formatCountdown(offer.expiresAt));
  useEffect(() => {
    setClock(formatCountdown(offer.expiresAt));
    const id = setInterval(() => setClock(formatCountdown(offer.expiresAt)), 250);
    return () => clearInterval(id);
  }, [offer.expiresAt]);

  useEffect(() => {
    playSoundEffect('/sounds/counterstrike.mp3', 0.7);
  }, []);

  const title = offer.battleType === 'enemy_hq'
    ? t('warEffort.defendHqTitle')
    : t('warEffort.defendCountryTitle', { country: countryDisplayName || offer.countryName });

  return createPortal(
    <div className="we-elite-defense-overlay" data-block-global-esc-back>
      <div
        className="we-elite-defense-dialog"
        style={{ backgroundImage: `url('${ELITE_FORCE_DIALOG_ART}')` }}
      >
        <div className="we-elite-defense-dialog-content">
          <CrossedSwordsHeader />
          <h3>{title}</h3>
          {actionError ? (
            <p className="we-elite-defense-error" role="alert">{actionError}</p>
          ) : null}
          <div className="we-elite-defense-countdown">{clock}</div>
          <div className="we-elite-defense-actions">
            <MenuPaintedButton
              className="war-effort-building-action-btn war-effort-building-action-btn--destroy"
              imagePath={WAR_EFFORT_LEAVE_MISSION_BUTTON_PATH}
              onClick={onRefuse}
              disabled={busy === true}
            >
              {t('warEffort.eliteDefenseRefuse')}
            </MenuPaintedButton>
            <MenuPaintedButton
              className="war-effort-building-action-btn"
              imagePath={WAR_EFFORT_PRIMARY_BUTTON_PATH}
              onClick={onOk}
              disabled={busy === true}
            >
              {t('warEffort.eliteDefenseOk')}
            </MenuPaintedButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
